<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
// digitt: DENG-7566 — manage conversation labels straight from the header
import ConversationHeaderLabels from './ConversationHeaderLabels.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
// digitt: DENG-7203 — toggle button for the user_information dashboard-app panel
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

// digitt: DENG-7667 — surface the assigned agent in the header meta row
const assignee = computed(() => props.chat.meta?.assignee || {});

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

// digitt: DENG-7396 — surface the conversation status as a badge in the header.
const conversationStatus = computed(() => currentChat.value.status);
const statusLabel = computed(() =>
  conversationStatus.value
    ? t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${conversationStatus.value}.TEXT`)
    : ''
);
// digitt: DENG-7396 — pill style mirrors the "LIVE" badge in the report
// overview MetricCard (bg -3 / text -11).
const STATUS_PILL = {
  [wootConstants.STATUS_TYPE.OPEN]: 'bg-n-teal-3 text-n-teal-11',
  [wootConstants.STATUS_TYPE.PENDING]: 'bg-n-amber-3 text-n-amber-11',
  [wootConstants.STATUS_TYPE.SNOOZED]: 'bg-n-blue-3 text-n-blue-11',
  [wootConstants.STATUS_TYPE.RESOLVED]: 'bg-n-slate-3 text-n-slate-11',
};

const statusPill = computed(
  () =>
    STATUS_PILL[conversationStatus.value] ??
    STATUS_PILL[wootConstants.STATUS_TYPE.RESOLVED]
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

// digitt: DENG-7203 — contact + user_information side-panel toggles live in the header now
const { uiSettings, updateUISettings } = useUISettings();

const isContactSidebarOpen = computed(
  () => !!uiSettings.value?.is_contact_sidebar_open
);
const toggleContactSidebar = () => {
  const isOpening = !isContactSidebarOpen.value;
  updateUISettings({
    is_contact_sidebar_open: isOpening,
    // only one side panel open at a time
    ...(isOpening
      ? { is_copilot_panel_open: false, is_user_information_open: false }
      : {}),
  });
};

const hasUserInformationApp = computed(() =>
  store.getters['dashboardApps/getRecords'].some(
    app => app.title === 'user_information'
  )
);
const isUserInformationOpen = computed(
  () => !!uiSettings.value?.is_user_information_open
);
const toggleUserInformation = () => {
  const isOpening = !isUserInformationOpen.value;
  updateUISettings({
    is_user_information_open: isOpening,
    // digitt: DENG-7203 — only one side panel open at a time
    ...(isOpening
      ? { is_contact_sidebar_open: false, is_copilot_panel_open: false }
      : {}),
  });
};

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-2 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
          <!-- digitt: DENG-7396 — conversation status badge (report-overview pill style) -->
          <span
            v-if="statusLabel"
            class="flex items-center flex-shrink-0 py-1 px-2 text-xs leading-none rounded"
            :class="statusPill"
          >
            {{ statusLabel }}
          </span>
        </div>

        <div
          class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <button
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span v-if="hasMultipleInboxes">•</span>
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <!-- digitt: DENG-7667 — assigned agent (person icon + name), read-only; matches the conversation-card badge -->
          <span v-if="assignee.name">•</span>
          <span
            v-if="assignee.name"
            class="flex items-center gap-0.5 min-w-0 text-n-blue-11"
          >
            <fluent-icon icon="person" class="size-4 flex-shrink-0" />
            <span class="truncate text-label-small">{{ assignee.name }}</span>
          </span>
          <span v-if="isSnoozed">•</span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end min-w-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <!-- digitt: DENG-7566 — add/remove labels without opening the sidebar -->
      <ConversationHeaderLabels />
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <!-- digitt: DENG-7203 — toggle the user_information dashboard-app panel -->
      <NextButton
        v-if="hasUserInformationApp"
        v-tooltip.bottom="$t('CONVERSATION.HEADER.USER_INFORMATION')"
        icon="i-ph-info"
        color="slate"
        :variant="isUserInformationOpen ? 'solid' : 'faded'"
        sm
        @click="toggleUserInformation"
      />
      <!-- digitt: DENG-7203 — contact panel toggle (moved from the floating switch) -->
      <NextButton
        v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.CONTACT')"
        icon="i-ph-user"
        color="slate"
        :variant="isContactSidebarOpen ? 'solid' : 'faded'"
        sm
        @click="toggleContactSidebar"
      />
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
